import { isEqual } from 'lodash';

import {
    PricesWizardExportData,
    PricesWizardInspectorData
} from '../../components/PricesWizardInspector/PricesWizardInspector';
import { CampaignPlatform, CampaignType } from '../../typings/domain';
import { PhrasesFeaturesData, PricesWizardData, PricesWizardType } from '../../typings/inspector';

export enum SimpleWizardType {
    search = 'search',
    net = 'net'
}

export enum ExtendedWizardType {
    search = 'search',
    net = 'net',
    mcbanner = 'mcbanner'
}

export class PricesWizardHelpers {
    static mapCommonPlatformsToSimpleWizardType(commonPlatforms: CampaignPlatform[]): SimpleWizardType[] {
        const types: Partial<Record<SimpleWizardType, boolean>> = commonPlatforms.reduce((acc, next) => {
            if (next === CampaignPlatform.SEARCH) {
                acc[SimpleWizardType.search] = true;
            }

            if (next === CampaignPlatform.CONTEXT) {
                acc[SimpleWizardType.net] = true;
            }

            if (next === CampaignPlatform.BOTH) {
                acc[SimpleWizardType.search] = true;
                acc[SimpleWizardType.net] = true;
            }

            return acc;
        }, {});

        return [SimpleWizardType.search, SimpleWizardType.net].filter(type => {
            return types[type];
        });
    }

    static mapCommonPlatformsAndCampaignTypesToExtendedWizardType(
        commonPlatforms: CampaignPlatform[],
        commonCampaignTypes: CampaignType[]
    ): ExtendedWizardType[] {
        if (isEqual(commonCampaignTypes, [CampaignType.MCBANNER])) {
            return [ExtendedWizardType.mcbanner];
        }

        const types: Set<ExtendedWizardType> = commonPlatforms.reduce((acc, next) => {
            if (next === CampaignPlatform.SEARCH) {
                acc.add(ExtendedWizardType.search);
            }

            if (next === CampaignPlatform.CONTEXT) {
                acc.add(ExtendedWizardType.net);
            }

            if (next === CampaignPlatform.BOTH) {
                acc.add(ExtendedWizardType.search);
                acc.add(ExtendedWizardType.net);
            }

            return acc;
        }, new Set());

        const wizardTypes = [ExtendedWizardType.search, ExtendedWizardType.net].filter(type => types.has(type));

        if (commonCampaignTypes.includes(CampaignType.MCBANNER)) {
            wizardTypes.push(ExtendedWizardType.mcbanner);
        }

        return wizardTypes;
    }

    static mapPricesWizardStateToExportData({
        wizardType,
        simple,
        extended
    }: PricesWizardData): PricesWizardExportData {
        const result: PricesWizardExportData = {
            wizardType
        };

        if (wizardType === PricesWizardType.simple) {
            result.simple = {};

            if (simple.search.checked) {
                result.simple.exactPriceSearch = Number(simple.search.price);
            }

            if (simple.net.checked) {
                result.simple.exactPriceContext = Number(simple.net.price);
            }
        } else {
            result.extended = {};

            if (extended.search.checked) {
                result.extended.search = {
                    setMaximum: extended.search.volume === 'max' ? true : undefined,
                    volume: extended.search.volume,
                    increasePercentage: Number(extended.search.increasePercentage),
                    priceLimit: Number(extended.search.priceLimit)
                };
            }

            if (extended.net.checked) {
                result.extended.net = {
                    coverage: extended.net.coverage,
                    increasePercentage: Number(extended.net.increasePercentage),
                    priceLimit: Number(extended.net.priceLimit)
                };
            }

            if (extended.mcbanner.checked) {
                result.extended.mcbanner = {
                    increasePercentage: Number(extended.mcbanner.increasePercentage),
                    priceLimit: Number(extended.mcbanner.priceLimit)
                };
            }
        }

        return result;
    }

    static getPricesWizardInspectorsData = (
        contextData: PhrasesFeaturesData
    ): Pick<PricesWizardInspectorData, 'simpleTypes' | 'extendedTypes'> => {
        const { commonPlatform, commonCampaignType } = contextData;

        return {
            simpleTypes: PricesWizardHelpers.mapCommonPlatformsToSimpleWizardType(commonPlatform),
            extendedTypes: PricesWizardHelpers.mapCommonPlatformsAndCampaignTypesToExtendedWizardType(
                commonPlatform,
                commonCampaignType
            )
        };
    };
}
